import { Box, Button, Dialog, LinearProgress, Stack, Typography } from '@mui/material';
import { X } from 'lucide-react';
import {
  downloadAndInstallUpdate,
  installerHandlesRestart,
  relaunchIntoNewVersion,
} from '../services/backend/updater.js';
import { useUpdateStore } from '../stores/updateStore';
import HandleTop from './ui/HandleTop';
import WindowBackground from './ui/WindowBackground';

const UpdateDialog = () => {
  const { pendingUpdate, updateStatus, downloadProgress, errorMessage, clearPendingUpdate } =
    useUpdateStore();

  const open =
    Boolean(pendingUpdate) ||
    ['no-update', 'check-failed', 'restart-required'].includes(updateStatus);

  const handleConfirm = () => {
    downloadAndInstallUpdate(pendingUpdate);
  };

  const handleClose = () => {
    if (updateStatus === 'downloading' || updateStatus === 'installed') return;
    clearPendingUpdate();
  };

  const handleRetry = () => {
    downloadAndInstallUpdate(pendingUpdate);
  };

  const progressPercent = downloadProgress?.total
    ? Math.round((downloadProgress.downloaded / downloadProgress.total) * 100)
    : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: { background: 'transparent', boxShadow: 'none' },
        },
      }}
    >
      <Box
        sx={{
          width: '480px',
          display: 'flex',
          flexDirection: 'column',
          gap: '1px',
          boxShadow:
            '0 33px 9px 0 rgba(67, 79, 86, 0.01), 0 21px 8px 0 rgba(67, 79, 86, 0.07), 0 12px 7px 0 rgba(67, 79, 86, 0.25), 0 5px 5px 0 rgba(67, 79, 86, 0.43), 0 1px 3px 0 rgba(67, 79, 86, 0.49)',
          background: 'transparent',
        }}
      >
        <HandleTop
          height="34px"
          onIconClick={
            updateStatus !== 'downloading' && updateStatus !== 'installed' ? handleClose : undefined
          }
          iconAriaLabel="Close update dialog"
        >
          <X />
        </HandleTop>
        <WindowBackground radius="16px">
          <Box
            sx={{
              flex: 1,
              padding: '24px 32px 32px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {updateStatus === 'idle' && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Update available — v{pendingUpdate?.version}
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  A new version is ready to download. The application will restart to apply it —
                  make sure you have no ongoing experiments or unsaved work before proceeding.
                </Typography>

                {pendingUpdate?.body && (
                  <Box
                    sx={{
                      backgroundColor: 'rgba(0,0,0,0.03)',
                      borderRadius: '4px',
                      padding: '12px',
                      maxHeight: '160px',
                      overflowY: 'auto',
                    }}
                  >
                    <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', display: 'block' }}>
                      {pendingUpdate.body}
                    </Typography>
                  </Box>
                )}

                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: '8px' }}>
                  <Button onClick={handleClose}>Not now</Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirm}
                    sx={{
                      backgroundColor: 'secondary.main',
                      '&:hover': { backgroundColor: 'secondary.dark' },
                    }}
                  >
                    Update now
                  </Button>
                </Stack>
              </>
            )}

            {updateStatus === 'downloading' && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Downloading update...
                </Typography>
                <LinearProgress
                  variant={progressPercent !== null ? 'determinate' : 'indeterminate'}
                  value={progressPercent ?? 0}
                  sx={{ borderRadius: '2px' }}
                />
                {progressPercent !== null && (
                  <Typography variant="caption">{progressPercent}%</Typography>
                )}
              </>
            )}

            {updateStatus === 'installed' && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Update installed
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {installerHandlesRestart()
                    ? 'The installer is now finishing the update. The application will close and reopen as the new version.'
                    : 'Restarting into the new version…'}
                </Typography>
                <LinearProgress sx={{ borderRadius: '2px' }} />
              </>
            )}

            {updateStatus === 'restart-required' && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Update installed
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  The update was installed but the application could not restart itself. Please
                  restart it to use the new version.
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: '8px' }}>
                  <Button onClick={handleClose}>Later</Button>
                  <Button
                    variant="contained"
                    onClick={relaunchIntoNewVersion}
                    sx={{
                      backgroundColor: 'secondary.main',
                      '&:hover': { backgroundColor: 'secondary.dark' },
                    }}
                  >
                    Restart now
                  </Button>
                </Stack>
              </>
            )}

            {updateStatus === 'no-update' && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  No update available
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  You are already running the latest version.
                </Typography>
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: '8px' }}>
                  <Button onClick={handleClose}>Close</Button>
                </Stack>
              </>
            )}

            {updateStatus === 'check-failed' && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Update server unavailable
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  The update server could not be reached. If this computer has no internet access,
                  updates can be installed manually: run the installer of a newer DatLab 9 version
                  obtained from Oroboros (e.g. via USB stick) over the existing installation.
                </Typography>
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: '8px' }}>
                  <Button onClick={handleClose}>Close</Button>
                </Stack>
              </>
            )}

            {updateStatus === 'error' && (
              <>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Update failed
                </Typography>
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  {errorMessage || 'An unexpected error occurred while updating.'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Your current installation is unchanged.
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: '8px' }}>
                  <Button onClick={handleClose}>Close</Button>
                  {pendingUpdate && (
                    <Button
                      variant="contained"
                      onClick={handleRetry}
                      sx={{
                        backgroundColor: 'secondary.main',
                        '&:hover': { backgroundColor: 'secondary.dark' },
                      }}
                    >
                      Retry
                    </Button>
                  )}
                </Stack>
              </>
            )}
          </Box>
        </WindowBackground>
      </Box>
    </Dialog>
  );
};

export default UpdateDialog;
